// bmp-sticky-atc.js — BMP sticky add-to-cart bar (custom theme code, no app).
//
// Data comes from snippets/bmp-sticky-atc.liquid (a JSON payload inside the
// product-information section). The bar element is created here and appended
// to <body>, OUTSIDE the section, so variant re-renders (section morphs) never
// reset its state. After every morph the payload/hidden variant input are
// re-read, so title/variant/price/image/button stay in sync — including after
// assets/bmp-size-aware-swatches.js changes the selection.
//
// The bar's button always delegates to the page's own main add-to-cart button
// (a click on it), so:
//   - size required & no size chosen -> assets/bmp-size-required.js refuses the
//     click, prompts, smoothly scrolls to the Size selector and focuses it;
//   - unavailable variant -> the main button is disabled, so is this one;
//   - otherwise -> the theme's own product-form submit runs and the cart
//     drawer opens exactly as with the main button.
(() => {
  if (window.__bmpStickyAtc) return;
  window.__bmpStickyAtc = true;

  const DATA_SELECTOR = 'script[data-bmp-sticky-atc-data]';
  const MAIN_BUTTON_SELECTOR = 'product-form-component [ref="addToCartButton"]';
  // BMP-HOOK-START:mobile-sticky-first-load
  const MOBILE_MQ = window.matchMedia('(max-width: 749px)');
  // BMP-HOOK-END:mobile-sticky-first-load
  // BMP-HOOK-START:ipad-sticky-first-load
  // iPad / tablet (750-1199px) uses the same first-load rule as phones.
  const TABLET_MQ = window.matchMedia('(min-width: 750px) and (max-width: 1199px)');
  // BMP-HOOK-END:ipad-sticky-first-load
  // BMP-HOOK-START:pdp-polish
  // Desktop (1200px+) uses the same first-load rule: the bar shows at load if Add to cart is below the fold.
  const DESKTOP_MQ = window.matchMedia('(min-width: 1200px)');
  // BMP-HOOK-END:pdp-polish

  /** @type {HTMLElement | null} */ let bar = null;
  /** @type {any} */ let data = null;
  /** @type {Element | null} */ let section = null;
  /** @type {HTMLButtonElement | null} */ let mainButton = null;
  /** @type {IntersectionObserver | null} */ let io = null;
  let scrolledPast = false;
  let rafId = 0;

  function readData() {
    const el = document.querySelector(DATA_SELECTOR);
    if (!el) return false;
    try {
      data = JSON.parse(el.textContent || '{}');
    } catch {
      return false;
    }
    section = document.getElementById(`shopify-section-${el.getAttribute('data-section-id')}`) || el.closest('.shopify-section');
    return true;
  }

  function findMainButton() {
    const scope = section || document;
    return /** @type {HTMLButtonElement | null} */ (scope.querySelector(MAIN_BUTTON_SELECTOR));
  }

  function build() {
    bar = document.createElement('div');
    bar.className = 'bmp-sticky-atc';
    bar.setAttribute('role', 'region');
    bar.setAttribute('aria-label', data.labels.region || 'Add to cart');
    bar.dataset.visible = 'false';
    bar.innerHTML = `
      <img class="bmp-sticky-atc__image" alt="" width="48" height="48" loading="lazy" decoding="async">
      <div class="bmp-sticky-atc__info">
        <span class="bmp-sticky-atc__title"></span>
        <span class="bmp-sticky-atc__meta"><span class="bmp-sticky-atc__variant"></span> <span class="bmp-sticky-atc__price"></span></span>
      </div>
      <button type="button" class="bmp-sticky-atc__button"></button>`;
    bar.querySelector('.bmp-sticky-atc__button').addEventListener('click', onBarClick);
    // Hidden bar must not be reachable by keyboard / screen readers.
    bar.inert = true;
    document.body.append(bar);
  }

  function formOf(button) {
    return button ? button.closest('product-form-component') : null;
  }

  function sizeGateClosed(form) {
    if (!form || !form.hasAttribute('data-bmp-size-required')) return false;
    return form.getAttribute('data-bmp-size-satisfied') !== 'true';
  }

  function currentVariantId(form) {
    const input = form ? form.querySelector('input[name="id"]') : null;
    return input ? String(input.value) : '';
  }

  // Write only on change: the body MutationObserver below would otherwise see
  // our own writes and re-schedule forever.
  function setText(el, value) {
    if (el && el.textContent !== value) el.textContent = value;
  }
  function setDisabled(el, value) {
    if (el.disabled !== value) el.disabled = value;
  }

  function sync() {
    rafId = 0;
    if (!readData() || !bar) return;
    const button = findMainButton();
    if (button !== mainButton) observeMainButton(button);
    const form = formOf(button);
    const variant = data.variants[currentVariantId(form)];
    const gateClosed = sizeGateClosed(form);

    setText(bar.querySelector('.bmp-sticky-atc__title'), data.title);
    const img = /** @type {HTMLImageElement} */ (bar.querySelector('.bmp-sticky-atc__image'));
    const src = (variant && variant.image) || data.fallbackImage;
    if (src && img.getAttribute('src') !== src) img.src = src;
    if (img.hidden !== !src) img.hidden = !src;

    let variantText = '';
    if (variant) {
      const parts = [];
      data.options.forEach((name, i) => {
        if (gateClosed && name === data.sizeOption) return;
        if (variant.options[i] && variant.options[i] !== 'Default Title') parts.push(variant.options[i]);
      });
      variantText = parts.join(' / ');
    }
    setText(bar.querySelector('.bmp-sticky-atc__variant'), variantText ? `${variantText} ·` : '');
    setText(bar.querySelector('.bmp-sticky-atc__price'), variant ? variant.price : '');

    const btn = /** @type {HTMLButtonElement} */ (bar.querySelector('.bmp-sticky-atc__button'));
    if (gateClosed) {
      setDisabled(btn, false);
      setText(btn, data.labels.selectSize);
    } else if (!button || button.disabled || !variant || !variant.available) {
      setDisabled(btn, true);
      setText(btn, data.labels.unavailable);
    } else {
      setDisabled(btn, false);
      setText(btn, data.labels.addToCart);
    }
  }

  function schedule() {
    if (!rafId) rafId = requestAnimationFrame(sync);
  }

  function setVisible(show) {
    if (!bar) return;
    const value = show ? 'true' : 'false';
    if (bar.dataset.visible === value) return;
    bar.dataset.visible = value;
    bar.inert = !show;
    document.body.classList.toggle('bmp-sticky-atc-open', show);
    if (show) document.body.style.setProperty('--bmp-sticky-atc-height', `${bar.offsetHeight}px`);
  }

  function observeMainButton(button) {
    mainButton = button;
    if (io) io.disconnect();
    if (!button) {
      setVisible(false);
      return;
    }
    // BMP-HOOK-START:mobile-sticky-first-load
    // Phones (max-width 749px): the bar is up from first load, whenever the
    // main button is NOT on screen (above OR below), and hides while the main
    // button itself is visible above the bar. Tablet/desktop keep the original
    // rule: show only after the main button has scrolled out ABOVE the viewport.
    const mobile = MOBILE_MQ.matches || TABLET_MQ.matches || DESKTOP_MQ.matches; // BMP-HOOK:ipad-sticky-first-load + pdp-polish (desktop too)
    const barH = (bar && bar.offsetHeight) || 72;
    io = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (mobile) {
          scrolledPast = !entry.isIntersecting;
        } else {
          // Show only once the main button has scrolled out of view ABOVE the
          // viewport; hide again as soon as it is back in view.
          scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        }
        setVisible(scrolledPast);
      }
    }, mobile ? { rootMargin: `0px 0px -${barH}px 0px` } : undefined);
    // BMP-HOOK-END:mobile-sticky-first-load
    io.observe(button);
  }

  function onBarClick() {
    const button = findMainButton();
    if (!button) return;
    // Delegate to the real button: size gate, form submit and cart drawer all
    // behave exactly as for the main button. Untrusted click is fine — the
    // size gate only needs trusted events to OPEN, never to refuse.
    if (button.disabled && !sizeGateClosed(formOf(button))) return;
    // BMP-HOOK-START:sticky-select-size
    // No size chosen yet: the bar scrolls to the Size picker itself (below the
    // sticky header), checks it got there, highlights it and focuses the first
    // size, instead of relying only on the gate's reveal via the main button.
    const gateForm = formOf(button);
    if (sizeGateClosed(gateForm)) {
      // When the shown colour has no variant in the (unchosen) default size,
      // the theme disables the main button, and .click() on a disabled
      // button fires nothing at all; that was the "does nothing" bug. Only
      // delegate when the button can receive the click, and always reveal.
      if (!button.disabled) button.click();
      else if (gateForm) gateForm.setAttribute('data-bmp-size-missing', 'true');
      revealSizePicker(gateForm);
      return;
    }
    // BMP-HOOK-END:sticky-select-size
    button.click();
    // If the size gate refused and the scroll put the Size picker on screen,
    // the main button comes back into view and the observer hides the bar.
  }

  // BMP-HOOK-START:sticky-select-size
  function sizeFieldset(form) {
    const scope = (form && form.closest('.shopify-section, dialog')) || section || document;
    const list = scope.querySelectorAll('[data-bmp-size-fieldset], [data-bmp-size-control]');
    for (const el of list) {
      const r = el.getBoundingClientRect();
      if (r.width > 0 && r.height > 0) return /** @type {HTMLElement} */ (el);
    }
    return null;
  }

  function headerOffset() {
    // Bottom edge of whatever sticky/fixed header covers the top of the page.
    let max = 0;
    for (const el of document.elementsFromPoint(window.innerWidth / 2, 4)) {
      for (let n = el; n && n !== document.body; n = n.parentElement) {
        const pos = getComputedStyle(n).position;
        if (pos === 'sticky' || pos === 'fixed') {
          if (bar && n === bar) break;
          max = Math.max(max, n.getBoundingClientRect().bottom);
          break;
        }
      }
    }
    return Math.min(Math.max(max, 0), 200);
  }

  function scrollParent(el) {
    for (let n = el.parentElement; n && n !== document.body && n !== document.documentElement; n = n.parentElement) {
      const oy = getComputedStyle(n).overflowY;
      if ((oy === 'auto' || oy === 'scroll' || oy === 'overlay') && n.scrollHeight > n.clientHeight + 1) return n;
    }
    return window;
  }

  function inView(el) {
    const r = el.getBoundingClientRect();
    return r.top >= headerOffset() - 2 && r.top < window.innerHeight * 0.6;
  }

  function revealSizePicker(form) {
    const target = sizeFieldset(form);
    if (!target) return;
    // The page scrolls on window on phones but inside .page-wrapper on wider
    // screens, so scroll whichever ancestor actually scrolls.
    const scroller = scrollParent(target);
    const delta = () => target.getBoundingClientRect().top - headerOffset() - 24;
    if (!inView(target)) scroller.scrollBy({ top: delta(), behavior: 'smooth' });
    // Safety net: if the smooth scroll was cancelled (iOS momentum, another
    // script's scroll), jump there.
    setTimeout(() => { if (!inView(target)) scroller.scrollBy({ top: delta(), behavior: 'auto' }); }, 700);
    target.classList.remove('bmp-satc-attention');
    void target.offsetWidth;
    target.classList.add('bmp-satc-attention');
    setTimeout(() => target.classList.remove('bmp-satc-attention'), 2400);
    const first = target.querySelector('input[type="radio"]:not([disabled]):not([aria-disabled="true"]), select');
    if (first instanceof HTMLElement) setTimeout(() => first.focus({ preventScroll: true }), 750);
  }
  // BMP-HOOK-END:sticky-select-size

  function init() {
    if (!readData()) return;
    build();
    sync();
    // One observer drives re-sync: section morphs (variant change / swatches),
    // the size gate's attribute changes, disabled state of the main button.
    new MutationObserver((records) => {
      for (const r of records) {
        if (!bar || !bar.contains(r.target)) {
          schedule();
          return;
        }
      }
    }).observe(document.body, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['disabled', 'value', 'data-bmp-size-satisfied', 'data-bmp-size-required'],
    });
    document.addEventListener('change', schedule, true);
    // BMP-HOOK-START:mobile-sticky-first-load
    // Crossing the phone breakpoint (rotation, resize) re-applies the rule.
    MOBILE_MQ.addEventListener('change', () => observeMainButton(findMainButton()));
    TABLET_MQ.addEventListener('change', () => observeMainButton(findMainButton())); // BMP-HOOK:ipad-sticky-first-load
    DESKTOP_MQ.addEventListener('change', () => observeMainButton(findMainButton())); // BMP-HOOK:pdp-polish
    // BMP-HOOK-END:mobile-sticky-first-load
    window.addEventListener('resize', () => {
      if (bar && bar.dataset.visible === 'true') document.body.style.setProperty('--bmp-sticky-atc-height', `${bar.offsetHeight}px`);
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
